import { bidPart1DataSchema } from "../../schemas/mhaForms/mca/bid.js";
import { responsibleDecisionMakerFields } from "./responsibleDecisionMakerFields.js";
export const bidPart1Template = {
    signing: {
        userOrTeamRequirement: {
            accreditation: ["doctor", "nurse", "hcpc", "social-worker"],
            teamType: [],
        },
    },
    formBuilderFields: [
        {
            type: "readonly",
            label: "Name and address of person",
            textFn: (data) => [
                data.patient?.name || "",
                [data.patient?.address, data.patient?.postalCode]
                    .filter(Boolean)
                    .join(", "),
            ],
            isReadOnlyPatientInfo: true,
        },
        {
            type: "practitioner",
            label: "Name and address of person completing this form",
            field: "user",
        },
        {
            type: "textbox",
            field: "decisionOutline",
            label: "Outline the decision to be made",
            rows: "5",
        },
        {
            type: "branch",
            field: "hasAssessedCapacity",
            label: "Have you assessed this person's capacity to make this decision?",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: ["foundLacksCapacity"],
                    fields: [
                        {
                            type: "branch",
                            field: "foundLacksCapacity",
                            label: "Did you find that the person lacks capacity to make this decision?",
                            branches: [
                                { fieldValue: true, label: "Yes", fields: [], fieldsOwned: [] },
                                {
                                    fieldValue: false,
                                    label: "No",
                                    fieldsOwned: [],
                                    fields: [
                                        {
                                            type: "label",
                                            bannerType: "info",
                                            label: [
                                                "A person who has capacity to make this decision is entitled to make it themselves. A best interests decision can only be made where the person lacks capacity to make this specific decision.",
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    fieldValue: false,
                    label: "No",
                    fieldsOwned: [],
                    fields: [
                        {
                            type: "label",
                            bannerType: "info",
                            label: [
                                "Consider undertaking a mental capacity assessment to establish whether the person lacks capacity to make this particular decision.",
                            ],
                        },
                    ],
                },
            ],
        },
        {
            type: "label",
            labelType: "subtitle",
            label: ["Substituted Decision Making"],
        },
        {
            type: "branch",
            field: "powerOfAttorneyOrCourtAppointedDeputy",
            label: "Does the person have a written lasting power of attorney that is valid and applicable, or a court-appointed deputy for health and welfare?",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: [],
                    fields: [
                        {
                            type: "label",
                            bannerType: "info",
                            label: [
                                "The attorney or deputy is the decision-maker and their decision should be followed, as long as they are acting in the person's best interests.",
                            ],
                        },
                    ],
                },
                { fieldValue: false, label: "No", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "branch",
            field: "advanceDecision",
            label: "Has the person made an advance decision that covers this decision?",
            branches: [
                {
                    fieldValue: true,
                    label: "Yes",
                    fieldsOwned: ["advanceDecisionDetails"],
                    fields: [
                        {
                            type: "label",
                            bannerType: "warning",
                            label: [
                                "A valid and applicable advance decision to refuse treatment can only apply to persons 18 or over.\nIf the advance decision is a refusal of life-sustaining treatment it must conform to the requirements of s25(5) & (6).\nIf unclear about the existence, validity or applicability of an advance decision, then life-sustaining treatment, or preventing a serious deterioration in the person's condition is lawful while the relevant issue is sought from the court s26(5).\nIf this is the case, seek legal advice.",
                            ],
                        },
                        {
                            type: "textbox",
                            field: "advanceDecisionDetails",
                            label: "Provide details of the advance decision",
                            rows: "5",
                        },
                    ],
                },
                { fieldValue: false, label: "No", fields: [], fieldsOwned: [] },
            ],
        },
        {
            type: "label",
            labelType: "subtitle",
            label: ["Best Interest Decision"],
        },
        {
            type: "label",
            bannerType: "info",
            label: [
                "A Best Interest Decision should not be used if:\n• There is a valid and applicable advance decision to refuse treatment (patients 18 and over only).\n• A donee(s) (under a LPA) or deputy (appointed by the court of protection) has decision making power in relation to this decision.\n• The treatment is for a mental disorder and regulated under Part 4 of the Mental Health Act. In this case, that treatment cannot be given under the MCA (see s28).",
            ],
        },
        {
            type: "branch",
            field: "regainCapacity",
            label: "If the person is likely to regain capacity, can the decision wait until then?",
            branches: [
                {
                    fieldValue: "can-wait",
                    label: "Yes - The decision can wait for the person to regain capacity",
                    fields: [],
                    fieldsOwned: [],
                },
                {
                    fieldValue: "cannot-wait",
                    label: "No - The decision is urgent and cannot wait for the person to regain capacity",
                    fields: [],
                    fieldsOwned: [],
                },
                {
                    fieldValue: "unlikely-to-regain",
                    label: "The person is unlikely to regain capacity",
                    fields: [],
                    fieldsOwned: [],
                },
            ],
        },
        {
            type: "textbox",
            field: "participationSteps",
            label: "How was the person supported to participate in the decision? If they have not been able to participate please explain why.",
            rows: "5",
        },
        {
            type: "textbox",
            field: "personsWishes",
            label: "Consider the person's wishes, feelings, beliefs, values and other factors that are relevant to the decision (all the things that the person would take into account)",
            rows: "5",
        },
        {
            type: "textbox",
            field: "consultationViews",
            label: "Who was consulted, and what views were taken into account? (Anyone named by the person interested in their welfare, any donee of an LPA, or any deputy)",
            rows: "5",
        },
        {
            type: "textbox",
            field: "optionsBenefitsRisks",
            label: "What options were considered, including benefits and risks, with regard to less restrictive principle?",
            rows: "5",
        },
        ...responsibleDecisionMakerFields(),
        {
            type: "textbox",
            field: "bestInterestDecisionAndReasoning",
            label: "What is the best interest decision, and why is it in the person's best interests?",
            rows: "5",
        },
        {
            type: "textbox",
            field: "otherRelevantInformation",
            label: "Any other relevant information",
            rows: "5",
        },
        {
            type: "confirmation-checkbox",
            field: "bestInterestDeclaration",
            checkboxLabel: "I confirm that I have considered the person's wishes and feelings, relevant circumstances, views of others, available options, risks and benefits, and the less restrictive option when making this best interests decision. I confirm that this decision has not been based merely on the person's age, appearance, condition or behaviour, and that, if it relates to life-sustaining treatment, I have not been motivated by a desire to bring about the person's death.",
        },
    ],
    dataSchema: bidPart1DataSchema,
};
export const bidV1Base = {
    id: "mca-bid",
    category: "mca",
    version: "1.0",
    status: "active",
    formName: "BID",
    section: "",
    parts: [bidPart1Template],
    description: "Best Interest Decision",
    displayTitle: "Best Interest Decision",
    extractGenericData: () => ({}),
};
//# sourceMappingURL=bid.js.map